import ContactFormField from '@/components/landingpage/contact-form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useClearFieldErrors } from '@/hooks/use-clear-field-errors';
import { useSessionExpiredFlag } from '@/hooks/use-session-expired-flag';
import { withFocusOnError } from '@/lib/inertia-form-options';
import { useAsset } from '@/utils/asset';
import { useForm } from '@inertiajs/react';
import { CheckCircle, Info, Loader2, Mail } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
    const { t } = useTranslation();
    const asset = useAsset();
    const { data, errors, setData, processing, post, clearErrors } = useForm({
        name: '',
        email: '',
        message: '',
    });
    useClearFieldErrors(data, clearErrors, errors);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const sessionExpiredCsrf = useSessionExpiredFlag('csrf');

    const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();

        post(
            route('contact-form.send'),
            withFocusOnError(
                {
                    preserveState: true,
                    onSuccess: () => {
                        setIsSubmitted(true);
                    },
                },
                { containerRef: formRef },
            ),
        );
    };

    return (
        <div
            id="kontakt"
            className="landing-defer-paint bg-linear-to-br from-gray-50 to-white py-12 sm:py-16 lg:py-24 dark:from-gray-950 dark:to-gray-900"
        >
            <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                {/* Header */}
                <div className="mx-auto mb-16 max-w-3xl text-center">
                    <h2 className="text-4xl font-semibold tracking-tight text-balance text-headline sm:text-5xl">{t('home.contact.title')}</h2>
                    <p className="mt-6 text-lg leading-relaxed text-paragraph">
                        {t('home.contact.description')} {t('home.contact.subtitle')}
                    </p>
                </div>

                <div className="grid gap-12 lg:grid-cols-2">
                    {/* Left Column - Contact Information */}
                    <div className="flex flex-col gap-10 p-4 lg:p-0">
                        {/* CEO Personal Touch */}
                        <div className="flex items-start gap-4">
                            <div className="shrink-0">
                                <div className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-hs-blue/20 dark:border-hs-green-light/20">
                                    <img
                                        src={asset('images/robert-litwak250.jpg')}
                                        alt="Geschäftsführer"
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                        width={64}
                                        height={64}
                                    />
                                </div>
                            </div>
                            <div className="flex-1">
                                <p className="text-base leading-relaxed text-paragraph">{t('home.contact.formIntro')}</p>
                            </div>
                        </div>

                        {/* Contact Methods */}
                        <div>
                            <h3 className="text-base leading-relaxed font-semibold text-headline">{t('home.contact.contactMethods')}</h3>
                            <div className="mt-6 space-y-6">
                                {/* Phone */}
                                {/*
                                <div className="flex items-start gap-3">
                                    <Phone className="h-5 w-5 shrink-0 text-hs-blue dark:text-hs-green-light" />
                                    <div className="flex-1">
                                        <a
                                            href={t('home.contact.phonelink')}
                                            className="text-base font-medium text-headline transition-colors hover:text-hs-blue dark:hover:text-hs-green-light"
                                        >
                                            {t('home.contact.phone.number')}
                                        </a>
                                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                                            {t('home.contact.phone.hours')}
                                        </p>
                                    </div>
                                </div>
                                */}

                                {/* Email */}
                                <div className="flex items-start gap-3">
                                    <Mail className="h-5 w-5 shrink-0 text-hs-blue dark:text-hs-green-light" />
                                    <div className="flex-1">
                                        <a
                                            href={t('home.contact.maillink')}
                                            className="text-base font-medium text-headline transition-colors hover:text-hs-blue dark:hover:text-hs-green-light"
                                        >
                                            {t('home.contact.email')}
                                        </a>
                                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{t('home.contact.emailResponseTime')}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="mt-8 border-t border-gray-200 pt-8 dark:border-gray-700">
                                <p className="text-base leading-relaxed font-semibold text-headline">{t('home.contact.address.title')}</p>
                                <address className="mt-4 text-base leading-relaxed text-paragraph not-italic">
                                    {t('home.contact.address.company')}
                                    <br />
                                    {t('home.contact.address.street')}
                                    <br />
                                    {t('home.contact.address.city')}
                                    <br />
                                    {t('home.contact.address.country')}
                                </address>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Contact Form Card */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xs ring-0 dark:border-gray-700/50 dark:bg-gray-800">
                        {!isSubmitted ? (
                            <>
                                {sessionExpiredCsrf && (
                                    <Alert className="mb-4">
                                        <Info className="h-4 w-4" />
                                        <AlertDescription>{t('session.sessionExpiredContactForm')}</AlertDescription>
                                    </Alert>
                                )}

                                <h3 className="text-base leading-relaxed font-semibold text-headline">{t('home.contact.form.headline')}</h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('home.contact.form.description')}</p>
                                <form
                                    ref={formRef}
                                    method="post"
                                    action={route('contact-form.send')}
                                    onSubmit={handleSubmit}
                                    className="mt-8 space-y-6"
                                >
                                    <ContactFormField
                                        name="name"
                                        label={t('home.contact.form.name')}
                                        placeholder={t('home.contact.form.name')}
                                        maxLength={200}
                                        value={data.name}
                                        onChange={(value) => setData('name', value)}
                                        error={errors.name}
                                        disabled={processing}
                                    />

                                    <ContactFormField
                                        name="email"
                                        type="email"
                                        label={t('home.contact.form.email')}
                                        placeholder={t('home.contact.form.email')}
                                        value={data.email}
                                        onChange={(value) => setData('email', value)}
                                        error={errors.email}
                                        disabled={processing}
                                    />

                                    <ContactFormField
                                        name="message"
                                        rows={5}
                                        label={t('home.contact.form.message')}
                                        placeholder={t('home.contact.form.message')}
                                        maxLength={10000}
                                        value={data.message}
                                        onChange={(value) => setData('message', value)}
                                        error={errors.message}
                                        disabled={processing}
                                    />

                                    <button
                                        type="submit"
                                        disabled={processing}
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-medium text-primary-foreground shadow-sm transition-all duration-150 hover:bg-primary/90 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                                    >
                                        {processing ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                {t('home.contact.form.submitting')}
                                            </>
                                        ) : (
                                            <>
                                                <Mail className="h-4 w-4" />
                                                {t('home.contact.form.submit')}
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="py-8 text-center">
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-hs-blue/10 dark:bg-hs-green-light/10">
                                    <CheckCircle className="h-8 w-8 text-hs-blue dark:text-hs-green-light" />
                                </div>
                                <h3 className="mt-6 text-lg font-semibold text-headline">{t('home.contact.form.success.title')}</h3>
                                <p className="mt-2 text-sm text-paragraph">{t('home.contact.form.success.message')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
